import type { Order, OrderItem } from '@/types/db'
// Helper biên ngày theo giờ VN (UTC+7) dùng chung toàn app — xem lib/utils.
// Re-export để import cũ từ order-utils vẫn hoạt động.
export { dayStartVn, dayEndVn } from '@/lib/utils'

const VND_FORMATTER = new Intl.NumberFormat('vi-VN')

export function formatVnd(amount: number | null | undefined): string {
  if (amount == null) return '—'
  return `${VND_FORMATTER.format(amount)} đ`
}

// Số COD shipper THU HỘ của 1 đơn (khớp computeCod backend + cột COD khi xuất Excel):
//   - Dropship (cod_amount != null) → dùng đúng số nhập tay (giá CTV bán khách cuối).
//   - KH thường → total + (khách trả ship ? shipping_fee : 0) − đã thu (paid). Kẹp ≥ 0.
export function computeOrderCod(
  o: Pick<Order, 'cod_amount' | 'shipping_paid_by' | 'shipping_fee' | 'total' | 'paid'>,
): number {
  if (o.cod_amount != null) return Math.max(0, Math.round(o.cod_amount))
  const shipShare = o.shipping_paid_by === 'customer' ? o.shipping_fee || 0 : 0
  return Math.max(0, Math.round(o.total + shipShare - (o.paid || 0)))
}

export function formatDateVi(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

export function formatTimeVi(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${hh}:${mi}`
}

export function formatDateTimeVi(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`
}

export function formatOrderCode(order: Pick<Order, 'code' | 'id'>): string {
  return order.code || `DH-${order.id}`
}

// Mã hiển thị ngắn DH0000001 (zero-pad 7 chữ số theo order.id). CHỈ để hiển
// thị (file 02); orders.code thật (DH-2026-00001) vẫn dùng để tìm kiếm/unique.
export function formatOrderShortCode(order: Pick<Order, 'id'>): string {
  return `DH${String(order.id).padStart(7, '0')}`
}

// Sum item subtotal — phòng khi DB chưa rebuild order.subtotal (vd query
// cũ trước RPC). Hiện tại RPC luôn tính, nhưng helper vẫn có thể useful.
export function sumItemsSubtotal(items: ReadonlyArray<OrderItem>): number {
  return items.reduce((acc, it) => acc + it.subtotal, 0)
}

export function remainingDue(order: Pick<Order, 'total' | 'paid'>): number {
  return Math.max(0, order.total - order.paid)
}

// Tách chuỗi link sản phẩm (lưu ở order_items.image_url) thành mảng URL.
// Mỗi link cách nhau bằng xuống dòng hoặc dấu phẩy. 1 sản phẩm có thể nhiều link.
export function parseImageUrls(raw: string | null | undefined): string[] {
  if (!raw) return []
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

